export const computeStatistics = (rows) => {
  if (!rows.length) return null

  const basicStats = computeBasicStats(rows)
  const tourStats = computeTourStats(rows)
  const cruiseStats = computeCruiseStats(rows)
  const shipStats = computeShipStats(rows)
  const staffStats = computeStaffStats(rows)
  const dateStats = computeDateStats(rows)
  const averageStats = computeAverageStats(rows, basicStats)
  const monthStats = computeMonthStats(rows)

  return {
    ...basicStats,
    ...tourStats,
    ...cruiseStats,
    ...shipStats,
    ...staffStats,
    ...dateStats,
    ...averageStats,
    ...monthStats
  }
}

const computeAverageStats = (rows, { totalTours, totalParticipants, totalShipCalls }) => {
  const totalStaff = rows.reduce((sum, row) => sum + (row.staff || 0), 0)

  return {
    avgParticipantsPerTour: totalTours ? totalParticipants / totalTours : 0,
    avgParticipantsPerShipCall: totalShipCalls ? totalParticipants / totalShipCalls : 0,
    avgToursPerShipCall: totalShipCalls ? totalTours / totalShipCalls : 0,
    totalStaff,
    participantsPerStaff: totalStaff ? totalParticipants / totalStaff : 0
  }
}

const computeMonthStats = (rows) => {
  const monthParts = rows.reduce((acc, row) => {
    const month = row.date?.slice(0, 7)
    if (month) acc[month] = (acc[month] || 0) + row.participants
    return acc
  }, {})

  const peakMonthEntry = Object.entries(monthParts).reduce((a, b) =>
    a[1] > b[1] ? a : b, ['', 0]
  )

  const peakMonth = peakMonthEntry[0]
    ? new Date(`${peakMonthEntry[0]}-01`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })
    : '—'

  return {
    peakMonth,
    peakMonthParticipants: peakMonthEntry[1] || 0
  }
}

const computeBasicStats = (rows) => {
  const totalTours = rows.length
  const totalParticipants = rows.reduce((sum, row) => sum + row.participants, 0)
  const uniqueCruiseLines = [...new Set(rows.map(row => row.cruiseLine))].filter(Boolean)
  const uniqueShips = [...new Set(rows.map(row => row.shipName))].filter(Boolean)
  
  const shipCallKeys = rows.map(row => `${row.shipName}|${row.date}`)
  const totalShipCalls = [...new Set(shipCallKeys)].length

  return {
    totalTours,
    totalParticipants,
    uniqueCruiseLines,
    uniqueShips,
    totalShipCalls
  }
}

const computeTourStats = (rows) => {
  const tourCounts = rows.reduce((acc, row) => {
    acc[row.code] = (acc[row.code] || 0) + 1
    return acc
  }, {})

  const tourTotals = rows.reduce((acc, row) => {
    acc[row.code] = (acc[row.code] || 0) + row.participants
    return acc
  }, {})

  const codeToName = rows.reduce((acc, row) => {
    acc[row.code] = row.name
    return acc
  }, {})

  const mostOccuredTourCode = Object.keys(tourCounts).reduce((a, b) => 
    tourCounts[a] > tourCounts[b] ? a : b, ''
  )
  
  const highestTourParticipantsCode = Object.keys(tourTotals).reduce((a, b) => 
    tourTotals[a] > tourTotals[b] ? a : b, ''
  )

  let singleTourMaxParticipants = 0
  let singleTourMaxName = '—'
  let singleTourMaxDate = '—'
  
  rows.forEach(row => {
    if (row.participants > singleTourMaxParticipants) {
      singleTourMaxParticipants = row.participants
      singleTourMaxName = row.name
      singleTourMaxDate = row.date
    }
  })

  return {
    mostOccuredTour: codeToName[mostOccuredTourCode] || '—',
    mostOccuredTourCount: tourCounts[mostOccuredTourCode] || 0,
    highestTourParticipants: codeToName[highestTourParticipantsCode] || '—',
    highestTourParticipantsCount: tourTotals[highestTourParticipantsCode] || 0,
    singleTourMaxParticipants,
    singleTourMaxName,
    singleTourMaxDate
  }
}

const computeCruiseStats = (rows) => {
  const cruiseLineDates = rows.reduce((acc, row) => {
    if (!acc[row.cruiseLine]) acc[row.cruiseLine] = new Set()
    acc[row.cruiseLine].add(row.date)
    return acc
  }, {})

  const cruiseLineParts = rows.reduce((acc, row) => {
    acc[row.cruiseLine] = (acc[row.cruiseLine] || 0) + row.participants
    return acc
  }, {})

  const mostOccuredCruiseLineEntry = Object.entries(cruiseLineDates).reduce((a, b) => 
    a[1].size > b[1].size ? a : b, ['', new Set()]
  )

  const cruiseLineMostParticipantsEntry = Object.entries(cruiseLineParts).reduce((a, b) => 
    a[1] > b[1] ? a : b, ['', 0]
  )

  return {
    mostOccuredCruiseLine: mostOccuredCruiseLineEntry[0] || '—',
    mostOccuredCruiseLineCount: mostOccuredCruiseLineEntry[1]?.size || 0,
    cruiseLineMostParticipants: cruiseLineMostParticipantsEntry[0] || '—',
    cruiseLineMostParticipantsCount: cruiseLineMostParticipantsEntry[1] || 0
  }
}

const computeShipStats = (rows) => {
  const shipDates = rows.reduce((acc, row) => {
    if (!acc[row.shipName]) acc[row.shipName] = new Set()
    acc[row.shipName].add(row.date)
    return acc
  }, {})

  const shipParts = rows.reduce((acc, row) => {
    acc[row.shipName] = (acc[row.shipName] || 0) + row.participants
    return acc
  }, {})

  const shipCallParts = rows.reduce((acc, row) => {
    const key = `${row.shipName}|${row.date}`
    acc[key] = (acc[key] || 0) + row.participants
    return acc
  }, {})

  const mostOccuredShipEntry = Object.entries(shipDates).reduce((a, b) => 
    a[1].size > b[1].size ? a : b, ['', new Set()]
  )

  const shipMostParticipantsEntry = Object.entries(shipParts).reduce((a, b) => 
    a[1] > b[1] ? a : b, ['', 0]
  )

  const shipSingleMaxEntry = Object.entries(shipCallParts).reduce((a, b) => 
    a[1] > b[1] ? a : b, ['', 0]
  )

  let shipSingleMaxName = '—'
  let shipSingleMaxDate = '—'
  
  if (shipSingleMaxEntry[0]) {
    [shipSingleMaxName, shipSingleMaxDate] = shipSingleMaxEntry[0].split('|')
  }

  return {
    mostOccuredShip: mostOccuredShipEntry[0] || '—',
    mostOccuredShipCount: mostOccuredShipEntry[1]?.size || 0,
    shipMostParticipants: shipMostParticipantsEntry[0] || '—',
    shipMostParticipantsCount: shipMostParticipantsEntry[1] || 0,
    shipSingleMaxParticipants: shipSingleMaxEntry[1] || 0,
    shipSingleMaxName,
    shipSingleMaxDate
  }
}

const computeStaffStats = (rows) => {
  const escortWork = rows.reduce((acc, row) => {
    if (row.escortName !== '—') {
      if (!acc[row.escortName]) acc[row.escortName] = new Set()
      acc[row.escortName].add(`${row.date}|${row.code}`)
    }
    return acc
  }, {})

  const guideWork = rows.reduce((acc, row) => {
    if (row.guideName !== '—') {
      if (!acc[row.guideName]) acc[row.guideName] = new Set()
      acc[row.guideName].add(`${row.date}|${row.code}`)
    }
    return acc
  }, {})

  const mostWorkedEscortEntry = Object.entries(escortWork).reduce((a, b) => 
    a[1].size > b[1].size ? a : b, ['', new Set()]
  )

  const mostWorkedGuideEntry = Object.entries(guideWork).reduce((a, b) => 
    a[1].size > b[1].size ? a : b, ['', new Set()]
  )

  const staffByDate = rows.reduce((acc, row) => {
    const date = row.date
    if (!acc[date]) acc[date] = { guides: new Set(), escorts: new Set() }
    if (row.guideName !== '—') acc[date].guides.add(row.guideName)
    if (row.escortName !== '—') acc[date].escorts.add(row.escortName)
    return acc
  }, {})

  const staffDates = Object.values(staffByDate)
  const totalUniqueStaffDates = staffDates.reduce((sum, date) => 
    sum + new Set([...date.guides, ...date.escorts]).size, 0
  )
  const totalUniqueGuidesDates = staffDates.reduce((sum, date) => 
    sum + date.guides.size, 0
  )
  const totalUniqueEscortsDates = staffDates.reduce((sum, date) => 
    sum + date.escorts.size, 0
  )

  return {
    mostWorkedEscort: mostWorkedEscortEntry[0] || '—',
    mostWorkedEscortCount: mostWorkedEscortEntry[1]?.size || 0,
    mostWorkedGuide: mostWorkedGuideEntry[0] || '—',
    mostWorkedGuideCount: mostWorkedGuideEntry[1]?.size || 0,
    totalUniqueStaffDates,
    totalUniqueGuidesDates,
    totalUniqueEscortsDates
  }
}

const computeDateStats = (rows) => {
  const dateParts = rows.reduce((acc, row) => {
    acc[row.date] = (acc[row.date] || 0) + row.participants
    return acc
  }, {})

  const dateMaxEntry = Object.entries(dateParts).reduce((a, b) => 
    a[1] > b[1] ? a : b, ['', 0]
  )

  const dateMaxShips = dateMaxEntry[0] !== '—' 
    ? [...new Set(rows
        .filter(row => row.date === dateMaxEntry[0] && row.participants > 0)
        .map(row => row.shipName)
      )]
    : []

  return {
    dateMaxParticipants: dateMaxEntry[1] || 0,
    dateMaxDate: dateMaxEntry[0] || '—',
    dateMaxShips
  }
}